import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { ProjectId } from 'api/models';
import {
  ProjectGlobalConversation,
  ProjectGlobalConversationsAPI,
} from 'api/project-conversations';
import { useConversationMetaLiveUpdateInReactQuery } from './conversation-name-live-update';
import { useRearrangeGlobalConversationInReactQuery } from './rearrange-conversation';
import {
  globalConversationQueryKey,
  GlobalConversationQueryResult,
} from './utils';

export const useProjectGlobalSubscription = (projectId: number): void => {
  useRearrangeGlobalConversationInReactQuery(projectId);
  useConversationMetaLiveUpdateInReactQuery(projectId);
};

const constructQueryResult = (
  conversations: ProjectGlobalConversation[],
): { conversations: ProjectGlobalConversation[]; unread: number } => ({
  conversations,
  unread: conversations.reduce(
    (count, conversation) => conversation.unreadMessages + count,
    0,
  ),
});

export const useProjectGlobalConversationQuery = <
  DefaultResult = GlobalConversationQueryResult,
>(
  projectId: ProjectId,
  options: UseQueryOptions<
    GlobalConversationQueryResult,
    unknown,
    DefaultResult,
    ReturnType<typeof globalConversationQueryKey['byProjectId']>
  > = {},
): UseQueryResult<DefaultResult, unknown> => {
  return useQuery(
    globalConversationQueryKey.byProjectId(projectId[1]),
    () =>
      ProjectGlobalConversationsAPI.getRelevantChat(projectId).then(
        constructQueryResult,
      ),
    {
      ...options,
      refetchOnMount: 'always',
      enabled: options.enabled,
    },
  );
};

export const useGlobalConversationLastSeenMutation = (): UseMutationResult<
  ProjectGlobalConversation,
  unknown,
  {
    entityId: number;
    entityType: ProjectGlobalConversation['entityType'];
    data: { lastSeenCommentCreatedAt: number };
  }
> => {
  return useMutation<
    ProjectGlobalConversation,
    unknown,
    {
      entityId: number;
      entityType: ProjectGlobalConversation['entityType'];
      data: { lastSeenCommentCreatedAt: number };
    }
  >(({ entityId, entityType, data }) =>
    ProjectGlobalConversationsAPI.updateConversationLastSeen(
      entityType,
      entityId,
      data,
    ),
  );
};
